import {
  Box,
  FormControl,
  FormLabel,
  Image,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Form, FormikProvider, useFormik } from 'formik';
import { useEffect, useMemo, useState } from 'react';
import * as Yup from 'yup';

import {
  CustomDrawerBackButton,
  CustomDrawerFooter,
  CustomDrawerHeader,
  CustomFormErrorMessage,
  PrimaryButton,
} from '../../../../components/custom';
import CustomDropdown from '../../../../components/custom/CustomDropdown/CustomDropdown';
import { InputGroupWithLoader } from '../../../../components/custom/CustomInputs';
import TransactionReceipt from '../../../../components/custom/TransactionReceipt';
import {
  handleNumericInput,
  handlePhoneNumericInput,
  zeroOnlyPhoneNumberValidation,
} from '../../../../constants/validationConstants';
import {
  currencyUnformat,
  numberFormatWithDecimals,
  sortBundles,
} from '../../../../helpers/utilityFunctions';
import useGetUIErrorMessage from '../../../../hooks/useGetUIErrorMessage';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { getBillTransactionsAsync } from '../../../../store/slices/billsPayment/billTransactionsSlice';
import { customerLookupAsync } from '../../../../store/slices/billsPayment/customerLookupSlice';
import { IGetBillers } from '../../../../store/slices/billsPayment/getBillersCategorySlice';
import {
  payBillAsync,
  resetPayBill,
} from '../../../../store/slices/billsPayment/payBillSlice';
import CreateTransactionPin from '../../../TransactionPin/CreateTransactionPin';
import { TransactionPinForm } from '../TransactionPinForm';
import ElectricityPurchaseSummary from './ElectricityPurchaseSummary';

interface IBuyElectricityFormProps {
  providers: IGetBillers[];
  selectedBillCategory: string;
  initialBillerName?: string;
  initialCustomerId?: string;
}

export const BuyElectricityForm = ({
  providers,
  selectedBillCategory,
  initialCustomerId,
  initialBillerName,
}: IBuyElectricityFormProps) => {
  const dispatch = useAppDispatch();

  const [page] = useState(1);
  const [limit] = useState(10);

  const [billerId, setBillerId] = useState('');

  const [selectedBillType, setSelectedBillType] = useState<string>('');

  const [, setIsFixedPrice] = useState(false);

  const [userHasPin, setUserHasPin] = useState(
    localStorage.getItem('userHasPin')
  );

  const [step, setStep] = useState(0);

  const [amountLimitError, setAmountLimitError] = useState('');

  const [insufficientBalanceError, setInsufficientBalanceError] = useState('');

  const { value } = useAppSelector((state) => state.auth);
  const { value: accountNumber } = useAppSelector(
    (state) => state.accountBalance
  );
  const { status: lookupStatus, value: lookupValue } = useAppSelector(
    (state) => state.customerLookup
  );

  const { value: acctBalValue } = useAppSelector(
    (state) => state.accountBalance
  );

  const { value: outletsValue } = useAppSelector((state) => state.getOutlets);

  const availableBalance = acctBalValue?.data?.availableBalance;
  const availableBalanceInNaira = availableBalance ? availableBalance / 100 : 0;

  const getMappedProviderForDropdown = (networkName: string) => {
    const networkOption = providerOptions.find(
      (option) => option.name === networkName
    );
    return {
      name: networkName,
      icon: networkOption ? <Image src={networkOption.icon} alt="" /> : null,
    };
  };

  useEffect(() => {
    dispatch(resetPayBill());
  }, [dispatch]);

  const providerOptions = useMemo(() => {
    return providers
      ?.filter((provider) => provider.name?.toLowerCase())
      .map((provider) => ({
        id: provider.id || '',
        name: provider.name || '',
        icon: provider.imageUrl || '',
      }));
  }, [providers]);

  const initialValues = {
    providerName: initialBillerName ?? '',
    meterType: '',
    customerId: initialCustomerId || '',
    phoneNumber: '',
    customerName: '',
    amount: '',
  };

  const validationSchema = Yup.object().shape({
    providerName: Yup.string().required('Provider is required'),
    meterType: Yup.string().required('Meter type is required'),
    customerId: Yup.string().required('Meter number is required'),
    phoneNumber: zeroOnlyPhoneNumberValidation,
    customerName: Yup.string().required('Customer name is required'),
    amount: Yup.string()
      .required('Amount is required')
      .test('min', 'Amount must be at least ₦500', (value) => {
        const numericValue = currencyUnformat(value);
        return numericValue >= 500;
      }),
  });

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: () => {
      // Do nothing
    },
  });

  const {
    errors,
    touched,
    values,
    isValid,
    getFieldProps,
    setFieldValue,
    handleSubmit,
  } = formik;

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;

    if (value === '' || (/^\+?\d*$/.test(value) && value.length <= 14)) {
      setFieldValue('phoneNumber', value);
    }
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    if (inputValue.endsWith('..') || /^(.*?\..*?){2}.*$/.test(inputValue)) {
      return;
    }

    const unformattedValue = currencyUnformat(inputValue);

    if (unformattedValue < 100) {
      setAmountLimitError('Amount cannot be lesser than ₦100');
    } else {
      setAmountLimitError('');
    }

    if (unformattedValue > availableBalanceInNaira) {
      setInsufficientBalanceError('Insufficient balance');
    } else {
      setInsufficientBalanceError('');
    }

    if (unformattedValue === 0) {
      setFieldValue('amount', '');
      setAmountLimitError('');
      setInsufficientBalanceError('');

      return;
    }

    let formattedValue = numberFormatWithDecimals(unformattedValue);

    if (inputValue.endsWith('.')) {
      formattedValue = `${numberFormatWithDecimals(unformattedValue)}.`;
    }

    setFieldValue('amount', formattedValue);
  };

  const handleMeterTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedMeter = e.target.value;
    const selectedMeterType = meterTypes.find(
      (meterType) => meterType.name === selectedMeter
    );

    if (selectedMeterType) {
      let billType = '';

      if (selectedMeterType.name.toLowerCase().includes('prepaid')) {
        billType = 'prepaid';
      } else if (selectedMeterType.name.toLowerCase().includes('postpaid')) {
        billType = 'postpaid';
      }

      setSelectedBillType(billType);
      setFieldValue('meterType', selectedMeter);
    }
  };

  const customerName = values.customerName;
  const customerAccountNo = accountNumber?.data?.accountNumber;
  const customerAddress = lookupValue?.data?.address || '';

  const accountNameFromStorage = localStorage.getItem('accountName');

  const outletId =
    outletsValue?.data?.find(
      (outlet) =>
        outlet?.account?.accountNumber === customerAccountNo &&
        outlet?.account?.accountName === accountNameFromStorage
    )?.id ?? '';

  const handleFinalSubmit = async (pinValues: { pin: string }) => {
    const selectedMeterType = meterTypes.find(
      (b) => b.name === values.meterType
    );
    const response = await dispatch(
      payBillAsync({
        outletId,
        billCategory: selectedBillCategory || '',
        customerId: values.customerId || '',
        billItemId: selectedMeterType?.id || '',
        customerAccountNo: customerAccountNo || 0,
        referenceId: lookupValue?.data?.referenceId || '',
        amount: currencyUnformat(values.amount),
        phoneNumber: value?.data?.user?.phoneNumber,
        pin: pinValues.pin || '',
        customerName: customerName,
        emailAddress: value?.data?.user?.email,
        billProvider: values.providerName,
        customerAddress: lookupValue?.data?.address || '',
      })
    );

    if (response?.meta?.requestStatus === 'fulfilled') {
      dispatch(
        getBillTransactionsAsync({
          accountNo: customerAccountNo,
          category: selectedBillCategory.toUpperCase(),
          page,
          limit,
        })
      );
    }
  };

  const handleMeterIdValidation = () => {
    if (values.customerId.length >= 5) {
      dispatch(
        customerLookupAsync({
          customerId: values.customerId,
          billerId: billerId || '',
          billType: selectedBillType || '',
          billerItemId:
            meterTypes.find((b) => b.name === values.meterType)?.id ?? '',
        })
      );
    }
  };

  useEffect(() => {
    if (
      lookupValue?.data?.name &&
      values.customerName !== lookupValue?.data?.name
    ) {
      setFieldValue('customerName', lookupValue?.data?.name);
      formik.validateField('customerName');
    }
  }, [
    dispatch,
    formik,
    lookupValue?.data?.name,
    setFieldValue,
    values.customerName,
  ]);

  const { status: paymentStatus, value: paymentValue } = useAppSelector(
    (state) => state.billsPayment
  );

  const meterTypes = useMemo(
    () =>
      providers?.find((n) => n.name === values.providerName)
        ?.currentProviderBillerItems || [],
    [providers, values.providerName]
  );

  const meterNames = meterTypes.map((meterType) => meterType.name);

  const sortedMeterTypes = sortBundles(meterNames);

  useEffect(() => {
    if (values.meterType) {
      setIsFixedPrice(false);
      const selectedMeterType = meterTypes.find(
        (b) => b.name === values.meterType
      );
      if (selectedMeterType?.isFixedPrice) {
        setFieldValue('amount', selectedMeterType?.amount?.toString());

        setIsFixedPrice(true);
      }
      setFieldValue('meterTypeId', selectedMeterType?.id || '');
    }
  }, [meterTypes, setFieldValue, values.meterType]);

  const handleGoForward = () => {
    if (meterTypes.length > 0 && isValid) {
      const selectedMeterType = meterTypes.find(
        (b) => b.name === values.meterType
      );
      const meterTypeAmount = Number(selectedMeterType?.amount || 0);

      if (meterTypeAmount <= availableBalanceInNaira) {
        setStep((prevStep) => prevStep + 1);
      } else {
        setInsufficientBalanceError(
          'Insufficient balance to complete this purchase.'
        );
      }
    }
  };

  useEffect(() => {
    if (values.providerName) {
      const provider = providerOptions.find(
        (option) => option.name === values.providerName
      );
      setBillerId(provider?.id ?? '');
    }
  }, [providerOptions, values.providerName]);

  const handleGoBack = () => {
    setStep((currentStep) => currentStep - 1);
  };

  const lookupErrorMessage = useGetUIErrorMessage({
    errors: lookupValue?.errors,
    message: lookupValue?.message,
  });

  const billDetails = {
    customerName: customerName,
    customerId: values.customerId ?? '',
    billCategory: 'ELECTRICITY',
    billItemId: meterTypes.find((b) => b.name === values.meterType)?.id ?? '',
    billerName: values.providerName,
    billerId: billerId ?? '',
  };

  const renderStep = () => {
    const steps = [
      <Box display="flex" flexDirection="column" py="18px">
        <CustomDrawerHeader startsFromBeginning>Electricity</CustomDrawerHeader>
        <FormikProvider value={formik}>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <Stack spacing={8} flex="1">
              <Box mb={6}>
                <Text fontWeight="500" color="#000" mb={1}>
                  Recipient Information
                </Text>
                <Text color="gray.300" fontSize=".875rem">
                  Streamline transactions with seamless information management
                </Text>
              </Box>

              <FormControl
                id="providerName"
                isInvalid={Boolean(touched.providerName && errors.providerName)}
              >
                <FormLabel>Provider</FormLabel>
                <CustomDropdown
                  options={providerOptions || []}
                  placeholder="Select an option"
                  getMappedNetwork={getMappedProviderForDropdown}
                  setFieldValue={setFieldValue}
                  value={values.providerName}
                  fieldValue="providerName"
                />
                <CustomFormErrorMessage>
                  {touched.providerName && errors.providerName}
                </CustomFormErrorMessage>
              </FormControl>

              <FormControl
                id="meterType"
                isInvalid={Boolean(touched.meterType && errors.meterType)}
              >
                <FormLabel>Meter type</FormLabel>
                <Select
                  disabled={!sortedMeterTypes?.length}
                  placeholder="Select an option"
                  isInvalid={Boolean(touched.meterType && errors.meterType)}
                  {...getFieldProps('meterType')}
                  onChange={handleMeterTypeChange}
                  textTransform="capitalize"
                >
                  {sortedMeterTypes?.map((selectedMeter, index) => (
                    <option key={index} value={selectedMeter}>
                      {selectedMeter}
                    </option>
                  ))}
                </Select>
                <CustomFormErrorMessage>
                  {touched.meterType && errors.meterType}
                </CustomFormErrorMessage>
              </FormControl>

              <FormControl
                id="customerId"
                isInvalid={
                  Boolean(touched.customerId && errors.customerId) ||
                  lookupStatus === 'failed'
                }
              >
                <FormLabel>Meter number</FormLabel>
                <InputGroupWithLoader isLoading={lookupStatus === 'loading'}>
                  <Input
                    {...getFieldProps('customerId')}
                    as="textarea"
                    placeholder="Enter your ID"
                    sx={{
                      pt: 4,
                    }}
                    onKeyDown={(e) => handleNumericInput(e, 16)} // change this when you find the max length
                    onBlur={handleMeterIdValidation}
                    maxLength={16}
                  />
                </InputGroupWithLoader>
                <CustomFormErrorMessage>
                  {(touched.customerId && errors.customerId) ||
                    (lookupStatus === 'failed' && lookupErrorMessage)}
                </CustomFormErrorMessage>
              </FormControl>

              <FormControl
                id="phoneNumber"
                isInvalid={Boolean(touched.phoneNumber && errors.phoneNumber)}
              >
                <FormLabel>Phone number</FormLabel>
                <Input
                  {...getFieldProps('phoneNumber')}
                  as="textarea"
                  placeholder="08023768491"
                  isInvalid={Boolean(touched.phoneNumber && errors.phoneNumber)}
                  sx={{
                    pt: 4,
                  }}
                  onChange={handlePhoneNumberChange}
                  onKeyDown={(e) => handlePhoneNumericInput(e, 11)}
                  maxLength={11}
                />
                <CustomFormErrorMessage>
                  {touched.phoneNumber && errors.phoneNumber}
                </CustomFormErrorMessage>
              </FormControl>

              <FormControl id="customerName">
                <FormLabel>Customer Name</FormLabel>
                <Input
                  isDisabled
                  {...getFieldProps('customerName')}
                  as="textarea"
                  placeholder="Your account name"
                  isInvalid={Boolean(
                    touched.customerName && errors.customerName
                  )}
                  sx={{
                    pt: 4,
                  }}
                />
                <CustomFormErrorMessage>
                  {touched.customerName && errors.customerName}
                </CustomFormErrorMessage>
              </FormControl>

              <FormControl
                id="amount"
                isInvalid={Boolean(
                  touched.amount &&
                    (insufficientBalanceError ||
                      amountLimitError ||
                      errors.amount)
                )}
              >
                <FormLabel>Amount</FormLabel>
                <InputGroup>
                  <InputLeftElement
                    pointerEvents="none"
                    children="₦"
                    height="100%"
                  />
                  <Input
                    {...getFieldProps('amount')}
                    placeholder="Enter amount"
                    onChange={handleAmountChange}
                    isInvalid={Boolean(
                      touched.amount &&
                        (insufficientBalanceError ||
                          amountLimitError ||
                          errors.amount)
                    )}
                  />
                </InputGroup>
                <CustomFormErrorMessage>
                  {touched.amount &&
                    (insufficientBalanceError ||
                      amountLimitError ||
                      errors.amount)}
                </CustomFormErrorMessage>
              </FormControl>
            </Stack>

            <CustomDrawerFooter>
              <PrimaryButton
                onClick={handleGoForward}
                type="submit"
                w="full"
                isDisabled={Boolean(insufficientBalanceError)}
              >
                Continue
              </PrimaryButton>
            </CustomDrawerFooter>
          </Form>
        </FormikProvider>
      </Box>,
      <>
        <CustomDrawerBackButton onClick={handleGoBack} />
        <ElectricityPurchaseSummary
          summary={{
            providerName: values.providerName,
            meterType: values.meterType,
            customerId: values.customerId,
            customerName: values.customerName,
            amount: values.amount,
            date: new Date(),
          }}
          customerAddress={customerAddress}
          handleSubmit={handleGoForward}
        />
      </>,
      <>
        <TransactionPinForm
          isSuccess={paymentStatus === 'success'}
          isLoading={paymentStatus === 'loading'}
          isError={paymentStatus === 'failed'}
          onSubmit={handleFinalSubmit}
          goToReceipt={handleGoForward}
          step={step}
          setStep={(currentStep) => setStep(currentStep)}
          statusBoxTitle={paymentValue?.message}
          statusBoxText=""
          billDetails={billDetails}
          twoFactor={{
            type: 'bill_payment',
            amountKobo: Math.round(currencyUnformat(values.amount) * 100),
          }}
        />
      </>,
      <TransactionReceipt
        date={new Date().toDateString()}
        amount={currencyUnformat(values.amount)}
        transactionType="Bill Payment"
        transactionReference={'----'}
        senderName={accountNameFromStorage ?? customerName}
        narration="test"
        provider={values.providerName}
        device={values.customerId}
        transactionFee="₦0.00"
        senderAccountNumber={customerAccountNo}
        showAmountInWords={false}
      />,
    ];

    return <>{steps[step]}</>;
  };

  return (
    <div>
      {!userHasPin ? (
        <CreateTransactionPin setUserHasPin={setUserHasPin} />
      ) : (
        renderStep()
      )}
    </div>
  );
};
